export type RecordingDemoPresetId =
  | "purchase-orders"
  | "invoices"
  | "bank-statements"
  | "receipts"
  | "document-parser";

export type RecordingDemoRelationship = {
  column: string;
  parentTableSlug: string;
  description: string;
};

export type RecordingDemoPresetTable = {
  id: string;
  name: string;
  slug: string;
  columns: string[];
  rowCount: number;
  relationship?: RecordingDemoRelationship;
};

export type RecordingDemoPreset = {
  id: RecordingDemoPresetId;
  label: string;
  projectName: string;
  documentLabel: string;
  primaryTableSlug: string;
  detailTableSlug: string;
  tables: RecordingDemoPresetTable[];
};

const table = (
  preset: RecordingDemoPresetId,
  name: string,
  columns: string[],
  rowCount: number,
  relationship?: RecordingDemoRelationship,
): RecordingDemoPresetTable => ({
  id: `recording-demo-${preset}-${name.toLowerCase().replaceAll("_", "-")}`,
  name: name
    .replaceAll("_", " ")
    .replace(/\b\w/g, (value) => value.toUpperCase()),
  slug: name.replaceAll("_", "-"),
  columns,
  rowCount,
  relationship,
});

export const RECORDING_DEMO_PRESETS: Record<
  RecordingDemoPresetId,
  RecordingDemoPreset
> = {
  "purchase-orders": {
    id: "purchase-orders",
    label: "Purchase Orders",
    projectName: "Purchase Order Workspace",
    documentLabel: "Purchase order",
    primaryTableSlug: "purchase-orders",
    detailTableSlug: "purchase-order-items",
    tables: [
      table(
        "purchase-orders",
        "suppliers",
        ["name", "address", "contact_email"],
        12,
      ),
      table(
        "purchase-orders",
        "purchase_orders",
        [
          "supplier_id",
          "purchase_order_number",
          "order_date",
          "expected_delivery_date",
          "currency",
          "total",
        ],
        30,
        {
          column: "supplier_id",
          parentTableSlug: "suppliers",
          description: "Supplier for this purchase order",
        },
      ),
      table(
        "purchase-orders",
        "purchase_order_items",
        [
          "purchase_order_id",
          "sku",
          "description",
          "quantity",
          "unit_price",
          "line_total",
        ],
        150,
        {
          column: "purchase_order_id",
          parentTableSlug: "purchase-orders",
          description: "Purchase order for this line item",
        },
      ),
    ],
  },
  invoices: {
    id: "invoices",
    label: "Invoices",
    projectName: "Invoice Workspace",
    documentLabel: "Invoice",
    primaryTableSlug: "invoices",
    detailTableSlug: "invoice-line-items",
    tables: [
      table("invoices", "vendors", ["name", "address", "tax_identifier"], 12),
      table(
        "invoices",
        "invoices",
        [
          "vendor_id",
          "invoice_number",
          "invoice_date",
          "due_date",
          "currency",
          "subtotal",
          "tax",
          "total",
        ],
        30,
        {
          column: "vendor_id",
          parentTableSlug: "vendors",
          description: "Vendor that issued this invoice",
        },
      ),
      table(
        "invoices",
        "invoice_line_items",
        ["invoice_id", "description", "quantity", "unit_price", "line_total"],
        150,
        {
          column: "invoice_id",
          parentTableSlug: "invoices",
          description: "Invoice for this line item",
        },
      ),
    ],
  },
  "bank-statements": {
    id: "bank-statements",
    label: "Bank Statements",
    projectName: "Bank Statement Workspace",
    documentLabel: "Bank statement",
    primaryTableSlug: "statements",
    detailTableSlug: "transactions",
    tables: [
      table(
        "bank-statements",
        "accounts",
        ["institution", "account_holder", "account_identifier", "currency"],
        12,
      ),
      table(
        "bank-statements",
        "statements",
        [
          "account_id",
          "period_start",
          "period_end",
          "opening_balance",
          "closing_balance",
        ],
        30,
        {
          column: "account_id",
          parentTableSlug: "accounts",
          description: "Account represented by this statement",
        },
      ),
      table(
        "bank-statements",
        "transactions",
        [
          "statement_id",
          "transaction_date",
          "posting_date",
          "description",
          "reference",
          "debit",
          "credit",
          "balance",
        ],
        150,
        {
          column: "statement_id",
          parentTableSlug: "statements",
          description: "Statement containing this transaction",
        },
      ),
    ],
  },
  receipts: {
    id: "receipts",
    label: "Receipts",
    projectName: "Receipt Workspace",
    documentLabel: "Receipt",
    primaryTableSlug: "receipts",
    detailTableSlug: "receipt-items",
    tables: [
      table("receipts", "merchants", ["name", "address"], 12),
      table(
        "receipts",
        "receipts",
        [
          "merchant_id",
          "transaction_date",
          "transaction_time",
          "payment_method",
          "subtotal",
          "tax",
          "tip",
          "total",
        ],
        30,
        {
          column: "merchant_id",
          parentTableSlug: "merchants",
          description: "Merchant for this receipt",
        },
      ),
      table(
        "receipts",
        "receipt_items",
        ["receipt_id", "description", "quantity", "unit_price", "item_total"],
        150,
        {
          column: "receipt_id",
          parentTableSlug: "receipts",
          description: "Receipt for this purchased item",
        },
      ),
    ],
  },
  "document-parser": {
    id: "document-parser",
    label: "Document Parser",
    projectName: "Document Parser Workspace",
    documentLabel: "Shipping document",
    primaryTableSlug: "shipping-invoices",
    detailTableSlug: "tracking-shipments",
    tables: [
      table(
        "document-parser",
        "shipping_invoices",
        [
          "invoice_number",
          "shipment_reference",
          "tracking_number",
          "carrier",
          "shipper_name",
          "consignee_name",
          "origin",
          "destination",
          "issue_date",
          "expected_delivery",
          "currency",
          "subtotal",
          "freight_charge",
          "insurance_charge",
          "tax",
          "total_amount",
          "status",
        ],
        30,
      ),
      table(
        "document-parser",
        "tracking_shipments",
        [
          "internal_fk_shipping_invoices_id",
          "invoice_number",
          "tracking_number",
          "checkpoint_sequence",
          "event_time",
          "event_status",
          "location",
          "facility_code",
          "notes",
        ],
        270,
        {
          column: "internal_fk_shipping_invoices_id",
          parentTableSlug: "shipping-invoices",
          description: "Shipping invoice for this tracking event",
        },
      ),
    ],
  },
};

export const RECORDING_DEMO_PRESET_OPTIONS = Object.values(
  RECORDING_DEMO_PRESETS,
);

export const DEFAULT_RECORDING_DEMO_PRESET_ID: RecordingDemoPresetId =
  "document-parser";

export function getRecordingDemoPreset(
  id?: string | null,
): RecordingDemoPreset {
  return (
    RECORDING_DEMO_PRESETS[id as RecordingDemoPresetId] ??
    RECORDING_DEMO_PRESETS[DEFAULT_RECORDING_DEMO_PRESET_ID]!
  );
}

export function recordingDemoCellValue(
  column: string,
  rowIndex: number,
  presetId: RecordingDemoPresetId,
): string | number {
  const index = rowIndex + 1;
  const samples: Record<string, Array<string | number>> = {
    name: ["Northstar Logistics", "Harbor Supply Co.", "Atlas Goods Ltd."],
    institution: ["Cedar Commercial Bank", "Atlantic Trust", "Union Finance"],
    account_holder: [
      "Northstar Logistics",
      "Atlas Goods Ltd.",
      "Harbor Supply Co.",
    ],
    account_identifier: ["•••• 4821", "•••• 7194", "•••• 3068"],
    address: [
      "18 Harbour Street, Kingston",
      "410 Biscayne Blvd, Miami",
      "72 King Street, Toronto",
    ],
    contact_email: [
      "orders@northstar.test",
      "procurement@harbor.test",
      "ops@atlas.test",
    ],
    tax_identifier: ["JM-784-221", "US-91-40382", "CA-770-194"],
    currency: ["USD", "JMD", "CAD"],
    carrier: ["Northstar Freight", "Blue Harbor Logistics", "Atlas Parcel"],
    shipper_name: [
      "Harbor Supply Co.",
      "Northstar Logistics",
      "Atlas Goods Ltd.",
    ],
    consignee_name: [
      "Marlow Retail",
      "Bayside Equipment",
      "Kingston Warehousing",
    ],
    origin: ["Kingston, JM", "Miami, US", "Toronto, CA"],
    destination: ["Miami, US", "Toronto, CA", "Kingston, JM"],
    status: ["In transit", "Destination scan", "Delivered"],
    description: [
      "Freight handling",
      "Document processing service",
      "Warehouse supplies",
    ],
    sku: ["NS-ARCH-12", "AC-DOC-04", "HS-USE-18"],
    payment_method: [
      "Corporate Visa •••• 4812",
      "Mastercard •••• 6190",
      "Cash",
    ],
    reference: ["ACH-88419", "CARD-10482", "WIRE-77201"],
    location: ["Kingston Hub", "Miami Gateway", "Toronto Depot"],
    event: ["Shipment received", "Customs cleared", "Out for delivery"],
    signed_by: ["A. Morgan", "S. Lewis", "D. Chen"],
  };
  if (samples[column])
    return samples[column]![rowIndex % samples[column]!.length]!;
  if (column === "invoice_number")
    return `INV-2607-${String(index).padStart(4, "0")}`;
  if (column === "tracking_number") return `SHP-${88420 + index}`;
  if (column === "purchase_order_number") return `PO-${48218 + index}`;
  if (column === "transaction_time")
    return `${String(8 + (index % 10)).padStart(2, "0")}:${String((index * 7) % 60).padStart(2, "0")}`;
  if (
    column.endsWith("_date") ||
    column.startsWith("period_") ||
    column === "order_date"
  ) {
    return `2026-07-${String(((index - 1) % 28) + 1).padStart(2, "0")}`;
  }
  if (
    column === "expected_delivery_date" ||
    column === "due_date" ||
    column === "expected_delivery"
  ) {
    return `2026-08-${String(((index - 1) % 28) + 1).padStart(2, "0")}`;
  }
  if (column === "event_sequence") return (rowIndex % 5) + 1;
  if (column === "quantity") return (rowIndex % 8) + 1;
  if (
    /subtotal|opening_balance|closing_balance|balance|total|tax|tip|debit|credit|unit_price/.test(
      column,
    )
  ) {
    return Number((18.5 + index * 7.35).toFixed(2));
  }
  return `${presetId.replaceAll("-", " ")} ${column.replaceAll("_", " ")} ${index}`;
}
